import {useQuery} from "@tanstack/react-query";
import {Token, UsersApi} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";

async function fetchElectronClientVersion(fullToken: Token): Promise<string | null> {
    const res = await new UsersApi(getConfig(fullToken)).getUsersMeElectronClientVersion();
    return res.electronClientVersion ?? null;
}

export function useGetElectronClientVersion(fullToken?: Token) {
    /*
     * Gate on the ACCOUNT, not the session.
     *
     * This used to gate on `isOnOverwolf`, so the "DevourPlay v{x}" label only
     * ever rendered INSIDE the desktop shell. That defeated the reason it
     * exists: support asks a user what version they're on, and the user is
     * usually in a browser when they're asking. The version is now a durable
     * account field (stamped at desktop login), so anyone with `hasDesktopApp`
     * has a real version to show, from any device.
     *
     * The gate is still needed — without one the hook fires for every logged-in
     * user on every account-menu render, spending a request on the large
     * majority who have no desktop app and nothing to display.
     */
    const {data: userProfileData} = useGetUserProfile(fullToken);
    const hasDesktopApp = !!userProfileData?.user?.hasDesktopApp;
    return useQuery({
        /*
         * Scope by token id so a logout-then-relogin as a different user doesn't
         * reuse the previous user's cached version while gcTime is still warm.
         */
        queryKey: ["electron-client-version", fullToken?.id ?? ""],
        queryFn: () => fetchElectronClientVersion(fullToken!),
        /*
         * Gate on `fullToken` (the object) rather than `fullToken?.token` (the
         * string). React Query's `enabled` flip false→true does not always
         * refetch when the cached result is `undefined` from a previously-
         * disabled state, so a brief refresh window where `.token` empties out
         * could leave the avatar version label stuck blank for up to gcTime
         * (5 min) after re-auth. The looser gate fires the call with whatever
         * token is in Redux at the time — backend returns 400 if it's stale,
         * React Query refetches on `staleTime`, and the version line lights
         * back up on the next tick. Same end-state UX, no edge-case dead zone.
         */
        enabled: !!fullToken && hasDesktopApp,
        staleTime: 60 * 1000,
        gcTime: 5 * 60 * 1000,
    });
}
